import { useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  uploadImage, 
  deleteImage, 
  updateCaption, 
  reorderImages,
  reorderMixedImages,
  linkJobImages, 
  unlinkJobImages,
  updateJobImageStatus,
  toggleImageInclusion,
  setPhotoPhase,
  bulkUpdateJobImages
} from '../api/imageApi'

export function useUploadImage(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ file, caption }: { file: File; caption?: string }) => uploadImage(galleryId, file, caption),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
    }
  })
}

export function useDeleteImage(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ imageId, type }: { imageId: number; type?: 'job_image' | 'gallery_photo' }) => deleteImage(imageId, type),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
  })
}

export function useUpdateCaption(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ imageId, caption, type }: { imageId: number; caption: string; type?: 'job_image' | 'gallery_photo' }) => updateCaption(imageId, caption, type),
    // Optimistic update on cache
    onMutate: async ({ imageId, caption }) => {
      const key = ['gallery-images', galleryId] as const
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<any>(key)
      if (previous?.length) {
        const next = previous.map((p: any) => (p.id === imageId ? { ...p, caption } : p))
        qc.setQueryData(key, next)
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['gallery-images', galleryId], ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
  })
}

export function useReorderImages(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (orderedLinkIds: number[]) => reorderImages(orderedLinkIds),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['gallery-images', galleryId] })
      // We could reorder locally if we had link IDs in cache; keeping server-truth here
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
  })
}

export function useReorderMixedImages(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (items: Array<{ id: number, type: 'job_image' | 'gallery_photo' }>) => reorderMixedImages(items),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
  })
}

export function useLinkJobImages(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (jobImageIds: number[]) => linkJobImages(galleryId, jobImageIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
  })
}

export function useUnlinkJobImages(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (linkIds: number[]) => unlinkJobImages(linkIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
  })
}

export function useUpdateJobImageStatus(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, status }: { jobId: number; status: 'approved' | 'rejected' | 'under_review' }) => 
      updateJobImageStatus(jobId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
    }
  })
}

export function useBulkUpdateJobImages(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, status }: { ids: number[]; status: 'approved' | 'rejected' }) => 
      bulkUpdateJobImages(ids, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
    }
  })
}

export function useToggleImageInclusion(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ galleryJobImageId, isIncluded }: { galleryJobImageId: number; isIncluded: boolean }) => 
      toggleImageInclusion(galleryJobImageId, isIncluded),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
    }
  })
}

export function useSetPhotoPhase(galleryId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      originalId,
      phase,
      imageType
    }: {
      originalId: number;
      phase: 'before' | 'during' | 'after';
      imageType: 'job_image' | 'gallery_photo'
    }) => setPhotoPhase(originalId, phase, imageType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gallery-images', galleryId] })
    }
  })
}

// Re-export from useGallery for convenience
export { useSetCoverImage } from './useGallery'
